import * as Sentry from '@sentry/react';
import { useMemo, useEffect } from 'react';

import SeoWrapper from '@/components/seo-wrapper/seo-wrapper';
import { Spinner } from '@/components/shared';

import dynamic from 'next/dynamic';
import PageNotFound from '@/pages/404';
import { IPageDefinition } from '@/components/authored-page-routes/authored-page-routes.interface';
import { type IApiTiming, trackSsrAPIs, toFixedNum } from '@iceberg/report-perfect-experience-analytics';
import { jsonLdPageTypeBuilder } from '@/utils/seo/helpers';
import { SpinnerOverlay } from '@/components/shared/spinner/spinner-overlay';
import { useScrollToContent } from '@/hooks';
import PageSSR from '@/components/common/common.component';

const PageCSR = dynamic(() => import('@/components/common/common.component').then((module) => module.default), {
  ssr: false,
  loading: () => <Spinner />,
});

const AuthoredPageRoutes: React.FC<IPageDefinition & { enableSSR: boolean; heroImageUrl?: string | null }> = ({
  viewDefinitionResults,
  themeTokens,
  pageType,
  ssrFetchData,
  heroImageUrl,
  ...rest
}) => {
  const { isScrolling } = useScrollToContent();
  
  const {
    name: ssrFetchApiName,
    duration: ssrFetchDurationMs,
    status: ssrFetchStatusCode,
  } = ssrFetchData ?? {};

  const initialApiTimings = useMemo<IApiTiming | undefined>(() => {
    if (ssrFetchApiName && ssrFetchDurationMs) {
      return {
        name: ssrFetchApiName,
        duration: toFixedNum(ssrFetchDurationMs),
        ...ssrFetchStatusCode ? { status: `${ssrFetchStatusCode}` } : {},
      };
    }
    return undefined;
  }, [ssrFetchApiName, ssrFetchDurationMs, ssrFetchStatusCode]);

  useEffect(() => {
    if (initialApiTimings) {
      trackSsrAPIs(initialApiTimings);
    }
  }, [initialApiTimings]);

  if (viewDefinitionResults?.isError) {
    console.error('BFF api failed - b3-ID: ' + viewDefinitionResults?.error?.b3);
    Sentry.captureMessage('BFF api failed - b3-ID: ' + viewDefinitionResults?.error?.b3);
    return <PageNotFound error={viewDefinitionResults?.error} />;
  }

  /** this console is needed to print the b3ID for every request **/
  console.log('bff data ====>', viewDefinitionResults?.bffData);
  const viewMetaData = viewDefinitionResults?.viewDefinition?.viewMetaData;
  const pageId = viewDefinitionResults?.pageID;
  const jsonLdScript = jsonLdPageTypeBuilder(pageType, viewDefinitionResults, viewMetaData);
  const pageProps = {
    pageId,
    viewDefinition: viewDefinitionResults?.viewDefinition,
    headerSection: viewDefinitionResults?.headerSection,
    themeTokens: themeTokens ?? undefined,
    ...rest
  }
  return (
    <SeoWrapper
      seoMetaData={viewMetaData}
      currentUrlBase={viewDefinitionResults?.currentUrlBase}
      jsonLdScript={jsonLdScript}
      heroImageUrl={heroImageUrl}
    >
      {rest?.enableSSR ? <PageSSR {...pageProps} /> : <PageCSR {...pageProps} />}
      <SpinnerOverlay isVisible={isScrolling} />
    </SeoWrapper>
  );
};

export default AuthoredPageRoutes;
